import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import type { RootState } from '@/store'

const baseQuery = fetchBaseQuery({
  baseUrl: '/api',
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).providerAuth.accessToken
    if (token) {
      headers.set('Authorization', `Bearer ${token}`)
    }
    return headers
  },
})

export interface ApiResponse<T> {
  success: boolean
  message?: string
  data: T
  totalCount?: number
}

export interface AplyRow {
  prgrm_aply_no: string
  prgrm_no: string
  mbr_no: string
  aply_stts_cd: string
  aply_stts_cd_nm: string
  frst_reg_dt3: string
  mbr_id: string
  mbr_nm: string
  mbr_se_cd: string
  mbr_se_cd_nm: string
  mbl_telno: string
  user_type_cd: string
  user_type_cd_nm: string
  prgrm_nm: string
  prtpnt_gubun_cd: string
  prtpnt_cnt: number
  schl_nm: string
  mngr_dept_nm: string
  mngr_mbl_telno: string
  mngr_nm: string
  user_mngr_mbl_telno: string
  prgm_dtl_no: string
  prgrm_end_ymd?: string
  rcpt_end_ymd?: string
  wait_no?: number
  confirm_prtpnt_cnt?: number
  wait_prtpnt_cnt?: number
  total_cnt?: number
  rnum?: number
  prgrs_stts_cd?: string
  operation_start_date?: string
  operation_end_date?: string
  operation_start_dtm?: string
  operation_end_dtm?: string
  org_nm?: string
}

export interface ProviderProgramRow {
  prgrm_no: string
  prgrm_mng_no: string
  prgrm_nm: string
  prgrm_ltype_cd_nm: string
  prgrm_stype_cd_nm: string
  recr_nope: string
  aplcnt_nope: string
  recr_bgng_ymd: string
  recr_end_ymd: string
  aply_mthd_cd_nm: string
  recr_mthd_cd: string
  recr_mthd_cd_nm: string
  recr_stts_nm: string
  recr_stts_cd: string
  mnl_recr_clsg_yn?: string
  clss_cnt?: number
  aply_clss_cnt?: number
  wait_aplcnt_nope?: number
  srvey_bgng_ymd?: string
  srvey_rslt_cnt?: number
  total_cnt?: number
  [key: string]: unknown
}

interface ApplicationListParams {
  miv_start_index?: number
  miv_end_index?: number
  prgrm_no?: string
  aply_stts_cd?: string
  prtpnt_gubun_cd?: string
  aply_stadt?: string
  aply_enddt?: string
  searchtxt?: string
  searchkey?: string
}

interface ApplicationListResponse {
  list: AplyRow[]
  totalCount: number
}

export interface PrtpntItem {
  mng_sn?: number
  prtpnt_nm: string
  gndr_cd: string
  gndr_cd_nm: string
  brth_yr?: string
  prtpnt_gubun_cd?: string
  /** 참가자별 신청상태코드 (RQ/AP/WA/CC/CN). 부분확정(PA) 표시에서 확정·대기 분류용. */
  aply_stts_cd?: string
}

export interface MbrChild {
  child_nm: string
  child_rel_nm: string
  child_gndr_cd: string
  child_gndr_cd_nm: string
  child_brdt: string
}

export interface ApplicationDetailRaw {
  prgrm_aply_no: string
  prgrm_no: string
  prgrm_nm: string
  frst_reg_dt3?: string
  aply_dt?: string
  aply_stts_cd: string
  aply_stts_cd_nm: string
  mbr_no: string
  mbr_nm: string
  mbr_mbl_telno?: string
  mbl_telno?: string
  mbr_eml_addr?: string
  eml_addr?: string
  prtpnt_list?: PrtpntItem[]
  remark?: string
  request_cn?: string
  wait_no?: number
  // 운영 종료 / 미확정 판정에 사용 (selectPrmgAplyInfoDetail 에서 함께 내려옴)
  rcpt_end_ymd?: string
  operation_start_date?: string
  operation_end_date?: string
  operation_start_dtm?: string
  operation_end_dtm?: string
  [key: string]: unknown
}

export interface SurveyResultRow {
  srvey_rslt_no: string
  prgrm_no: string
  mbr_no: string
  rslt_nm: string
  rslt_telno: string
  rslt_dt2: string
  mbr_id: string
  mbr_se_cd: string
  mbr_se_cd_nm: string
  mbr_nm2: string
  schl_nm: string
  schl_nm2: string
  bsc_006_cd_v: string
  prtpnt_cnt?: number
  srvey_dt: string
  total_cnt?: number
  rnum?: number
}

export interface SurveyAnswerItem {
  srvey_rslt_dtl_no: string
  srvey_rslt_no: string
  srvey_item_no: string
  ans_cn: string
  item_se_cd: string
  item_ttl: string
  item_expln: string
  item_optn: string
  sort_sn: number
}

export interface SurveyResultDetail {
  srvey_rslt_no: string
  prgrm_no: string
  prgrm_nm: string
  mbr_no: string
  mbr_id: string
  mbr_nm2: string
  mbr_se_cd_nm: string
  rslt_nm: string
  rslt_telno: string
  rslt_dt2: string
  schl_nm2: string
  answers: SurveyAnswerItem[]
  [key: string]: unknown
}

export interface SurveyStatsAnswer {
  ans_cn: string
  mbr_id: string
  rslt_dt2: string
}

export interface SurveyStatsItem {
  srvey_item_no: string
  item_se_cd: string
  item_ttl: string
  item_expln: string
  item_optn: string
  esntl_yn: string
  sort_sn: number
  answers: SurveyStatsAnswer[]
}

export interface SurveyStatsSummary {
  prgrm_no: string
  prgrm_nm: string
  srvey_bgng_ymd: string
  srvey_end_ymd: string
  aplcnt_nope: number
  respondent_cnt: number
}

export interface SurveyStatsData {
  summary: SurveyStatsSummary
  items: SurveyStatsItem[]
}

interface SurveyResultListParams {
  prgmNo: string
  page?: number
  size?: number
  srvey_stadt?: string
  srvey_enddt?: string
  mbr_se_cd?: string
  searchkey?: string
  searchtxt?: string
}

interface SurveyResultListResponse {
  list: SurveyResultRow[]
  totalCount: number
}

export const providerApi = createApi({
  reducerPath: 'providerApi',
  baseQuery,
  tagTypes: ['ProviderApplication'],
  endpoints: (builder) => ({
    getApplicationDetail: builder.query<ApplicationDetailRaw, { aplyNo: string; prgmNo: string }>({
      query: ({ aplyNo, prgmNo }) => `/admin/prmg/application/${aplyNo}/${prgmNo}`,
      transformResponse: (response: ApiResponse<ApplicationDetailRaw>) => response.data,
      providesTags: ['ProviderApplication'],
    }),
    getMbrChildren: builder.query<MbrChild[], string>({
      query: (mbrNo) => `/admin/prmg/application/children/${mbrNo}`,
      transformResponse: (response: ApiResponse<MbrChild[]>) => response.data || [],
    }),
    getApplicationList: builder.query<ApplicationListResponse, ApplicationListParams>({
      query: (params) => {
        const sp = new URLSearchParams()
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
        })
        return `/hmpg/provider/prgm/applicants?${sp.toString()}`
      },
      transformResponse: (response: ApiResponse<AplyRow[]>) => {
        const list = response.data || []
        const totalCount = list.length > 0 && list[0].total_cnt ? Number(list[0].total_cnt) : 0
        return { list, totalCount }
      },
      providesTags: ['ProviderApplication'],
    }),
    getSurveyStats: builder.query<SurveyStatsData, string>({
      query: (prgmNo) => `/admin/prgm/${prgmNo}/survey-stats`,
      transformResponse: (response: ApiResponse<SurveyStatsData>) => response.data,
    }),
    getSurveyResultDetail: builder.query<SurveyResultDetail, string>({
      query: (rsltNo) => `/admin/prgm/survey-results/${rsltNo}`,
      transformResponse: (response: ApiResponse<SurveyResultDetail>) => response.data,
    }),
    getSurveyResultList: builder.query<SurveyResultListResponse, SurveyResultListParams>({
      query: ({ prgmNo, ...params }) => {
        const sp = new URLSearchParams()
        Object.entries(params).forEach(([k, v]) => {
          if (v !== undefined && v !== null && v !== '') sp.set(k, String(v))
        })
        return `/admin/prgm/${prgmNo}/survey-results/page?${sp.toString()}`
      },
      transformResponse: (response: { data: SurveyResultRow[]; totalCount?: number }) => {
        const list = response.data || []
        const totalCount = response.totalCount ?? (list.length > 0 && list[0].total_cnt ? Number(list[0].total_cnt) : 0)
        return { list, totalCount }
      },
    }),
    getProviderProgramList: builder.query<{ list: ProviderProgramRow[]; totalCount: number }, Record<string, unknown>>({
      query: (params) => {
        const sp = new URLSearchParams()
        Object.entries(params).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== '') sp.set(k, String(v)) })
        return `/public/prgm/page?${sp.toString()}`
      },
      transformResponse: (response: { data: ProviderProgramRow[]; totalCount?: number }) => {
        const list = response.data || []
        const totalCount = response.totalCount ?? (list.length > 0 ? Number((list[0] as Record<string, unknown>).total_cnt) || 0 : 0)
        return { list, totalCount }
      },
      providesTags: ['ProviderApplication'],
    }),
    getProviderProgramDetail: builder.query<ApiResponse<Record<string, unknown>>, string>({
      query: (prgmNo) => `/public/prgm/${prgmNo}`,
      providesTags: (_r, _e, id) => [{ type: 'ProviderApplication', id }],
    }),
    getProviderProgramSessions: builder.query<ApiResponse<Record<string, unknown>[]>, string>({
      query: (prgmNo) => `/public/prgm/${prgmNo}/sessions`,
      providesTags: (_r, _e, id) => [{ type: 'ProviderApplication', id }],
    }),
    getProviderSurveyItems: builder.query<ApiResponse<Record<string, unknown>[]>, string>({
      query: (prgmNo) => `/hmpg/provider/prgm/${prgmNo}/survey-items`,
      providesTags: (_r, _e, id) => [{ type: 'ProviderApplication', id }],
    }),
    createProgram: builder.mutation<ApiResponse<{ prgrm_no: string }>, Record<string, unknown>>({
      query: (body) => ({ url: '/hmpg/provider/prgm', method: 'POST', body }),
      invalidatesTags: ['ProviderApplication'],
    }),
    updateProviderProgram: builder.mutation<ApiResponse<void>, { prgm_no: string } & Record<string, unknown>>({
      query: ({ prgm_no, ...body }) => ({ url: `/hmpg/provider/prgm/${prgm_no}`, method: 'PUT', body }),
      invalidatesTags: ['ProviderApplication'],
    }),
    deleteProviderProgram: builder.mutation<ApiResponse<void>, string>({
      query: (prgmNo) => ({ url: `/hmpg/provider/prgm/${prgmNo}`, method: 'DELETE' }),
      invalidatesTags: ['ProviderApplication'],
    }),
    saveProgramSessions: builder.mutation<ApiResponse<void>, { prgmNo: string; sessions: Record<string, unknown>[] }>({
      query: ({ prgmNo, sessions }) => ({
        url: `/hmpg/provider/prgm/${prgmNo}/sessions`,
        method: 'POST',
        body: sessions,
      }),
      invalidatesTags: ['ProviderApplication'],
    }),
    saveProviderSurveyItems: builder.mutation<ApiResponse<void>, { prgmNo: string; items: Record<string, unknown>[] }>({
      query: ({ prgmNo, items }) => ({
        url: `/hmpg/provider/prgm/${prgmNo}/survey-items`,
        method: 'POST',
        body: items,
      }),
      invalidatesTags: ['ProviderApplication'],
    }),
    verifyProviderPassword: builder.mutation<ApiResponse<null>, { password: string }>({
      query: (data) => ({
        url: '/hmpg/memb/verify-password',
        method: 'POST',
        body: { pswd: data.password },
      }),
    }),
    getProviderProfile: builder.query<ApiResponse<Record<string, unknown>>, void>({
      query: () => '/hmpg/memb/provider-profile',
      providesTags: ['ProviderApplication'],
    }),
    updateProviderProfile: builder.mutation<ApiResponse<Record<string, unknown>>, Record<string, unknown>>({
      query: (data) => ({
        url: '/hmpg/memb/profile',
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['ProviderApplication'],
    }),
    withdrawProvider: builder.mutation<ApiResponse<null>, void>({
      query: () => ({
        url: '/hmpg/memb/withdraw',
        method: 'DELETE',
      }),
    }),
    uploadProviderFile: builder.mutation<ApiResponse<{ file_group_no: string; file_no: string }>, { file: File; file_group_no?: string }>({
      query: ({ file, file_group_no }) => {
        const formData = new FormData()
        formData.append('file', file)
        if (file_group_no) formData.append('file_group_no', file_group_no)
        return { url: '/admin/comm/files/upload', method: 'POST', body: formData }
      },
    }),
    providerReconsent: builder.mutation<ApiResponse<null>, void>({
      query: () => ({
        url: '/auth/reconsent',
        method: 'POST',
      }),
    }),
    providerChangePassword: builder.mutation<ApiResponse<null>, { currentPassword: string; newPassword: string }>({
      query: (data) => ({
        url: '/auth/user/change-password',
        method: 'POST',
        body: { cur_pswd: data.currentPassword, new_pswd: data.newPassword },
      }),
    }),
    providerPostponePassword: builder.mutation<ApiResponse<null>, void>({
      query: () => ({
        url: '/auth/postpone-password',
        method: 'POST',
      }),
    }),
  }),
})

export const {
  useGetApplicationDetailQuery,
  useGetApplicationListQuery,
  useGetMbrChildrenQuery,
  useGetSurveyResultListQuery,
  useGetSurveyResultDetailQuery,
  useGetSurveyStatsQuery,
  useCreateProgramMutation,
  useSaveProgramSessionsMutation,
  useSaveProviderSurveyItemsMutation,
  useUploadProviderFileMutation,
  useVerifyProviderPasswordMutation,
  useGetProviderProfileQuery,
  useUpdateProviderProfileMutation,
  useGetProviderProgramListQuery,
  useGetProviderProgramDetailQuery,
  useGetProviderProgramSessionsQuery,
  useGetProviderSurveyItemsQuery,
  useUpdateProviderProgramMutation,
  useDeleteProviderProgramMutation,
  useWithdrawProviderMutation,
  useProviderReconsentMutation,
  useProviderChangePasswordMutation,
  useProviderPostponePasswordMutation,
} = providerApi
